import { isNativeApp } from './platform';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Available primary color palettes. Keep in sync with .theme-<name> classes
// defined in src/index.css and the swatches rendered in Settings.
export const THEME_COLORS = [
  { id: 'blue', swatch: '#2563eb' },
  { id: 'lightBlue', swatch: '#0ea5e9' },
  { id: 'green', swatch: '#10b981' },
  { id: 'pink', swatch: '#ec4899' },
  { id: 'lightPink', swatch: '#f472b6' },
  { id: 'yellow', swatch: '#eab308' },
  { id: 'orange', swatch: '#f97316' },
  { id: 'black', swatch: '#171717' },
  { id: 'white', swatch: '#f1f5f9' }
];

const MODES = ['light', 'dark', 'system'];

const MODE_KEY = 'life_manager_theme_mode';
const COLOR_KEY = 'life_manager_theme_color';

// The accent each palette actually paints with — Tailwind's primary-600, the
// shade used for today's circle and the primary buttons. (THEME_COLORS.swatch
// is the lighter dot shown in Settings, not this.) Keep in sync with the
// .theme-<name> blocks in src/index.css.
export const THEME_ACCENTS = {
  blue: '#2563eb',
  lightBlue: '#0284c7',
  green: '#059669',
  pink: '#db2777',
  lightPink: '#e11d48',
  yellow: '#ca8a04',
  orange: '#ea580c',
  black: '#525252',
  white: '#475569'
};

const ThemeContext = createContext({
  mode: 'system',
  color: 'blue',
  resolvedMode: 'light',
  setMode: () => {},
  setColor: () => {}
});

const readStored = (key, fallback) => {
  try {
    const v = window.localStorage.getItem(key);
    return v || fallback;
  } catch (_) {
    return fallback;
  }
};

const getSystemPref = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// The theme as it is actually being painted right now, readable outside React.
// The home-screen widget needs this: dark mode is an in-app setting here, so a
// widget that merely followed iOS would stay white while the app is dark.
export const currentTheme = () => {
  const stored = readStored(MODE_KEY, 'system');
  const mode = MODES.includes(stored) ? stored : 'system';
  const color = readStored(COLOR_KEY, 'blue');
  return {
    mode: mode === 'system' ? getSystemPref() : mode,
    accent: THEME_ACCENTS[color] || THEME_ACCENTS.blue
  };
};

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    const stored = readStored(MODE_KEY, 'system');
    return MODES.includes(stored) ? stored : 'system';
  });
  const [color, setColorState] = useState(() => {
    const stored = readStored(COLOR_KEY, 'blue');
    return THEME_COLORS.some((c) => c.id === stored) ? stored : 'blue';
  });
  const [systemPref, setSystemPref] = useState(getSystemPref);

  // Listen to OS-level theme changes (for mode === 'system')
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPref(e.matches ? 'dark' : 'light');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const resolvedMode = mode === 'system' ? systemPref : mode;

  // Apply classes to <html>
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolvedMode === 'dark');
    // Native shell: match the iOS status-bar text to the theme (light text
    // on dark, dark text on light). Dynamic import keeps the web bundle
    // free of the plugin.
    if (isNativeApp) {
      import('@capacitor/status-bar')
        .then(({ StatusBar, Style }) =>
          StatusBar.setStyle({
            style: resolvedMode === 'dark' ? Style.Dark : Style.Light
          })
        )
        .catch(() => {});
    }

    // Remove any previous theme-* class, then add the current one.
    Array.from(root.classList)
      .filter((c) => c.startsWith('theme-'))
      .forEach((c) => root.classList.remove(c));
    root.classList.add(`theme-${color}`);
  }, [resolvedMode, color]);

  // Persist
  useEffect(() => {
    try {
      window.localStorage.setItem(MODE_KEY, mode);
    } catch (_) {}
  }, [mode]);
  useEffect(() => {
    try {
      window.localStorage.setItem(COLOR_KEY, color);
    } catch (_) {}
  }, [color]);

  const setMode = useCallback((next) => {
    if (MODES.includes(next)) setModeState(next);
  }, []);
  const setColor = useCallback((next) => {
    if (THEME_COLORS.some((c) => c.id === next)) setColorState(next);
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, color, resolvedMode, setMode, setColor }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
